import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

// services
import {ConfigManager} from '../../services/ConfigManager';
import {CreditBookApi} from '../../services/ApiService';
import {UserConfigManager} from '../../services/UserConfigManager';

// components
import {LoadingPage} from '../../../pages/LoadingPage';

// styles
import {Container, Title, Wrapper} from './ConfigLoader-styles';

type ConfigLoaderProps = {
  children: React.ReactNode;
};

/**
 * This is the main component of the application.
 */
function ConfigLoader({children}: ConfigLoaderProps) {
  const {t} = useTranslation();
  const [loadingPortalConfig, setLoadingPortalConfig] = useState(true);
  const [loadingUserConfig, setLoadingUserConfig] = useState(true);
  const [error, setError] = useState(false);

  /**
   * Fetch the portal configuration
   */
  const fetchPortalConfig = async () => {
    try {
      const {data} = await CreditBookApi.portalConfig.getPortalConfig();
      ConfigManager.setConfig(data);
    } catch {
      setError(true);
    } finally {
      setLoadingPortalConfig(false);
    }
  };

  /**
   * Fetch the user configuration
   * If the user is not authenticated, we skip fetching and set loading to false
   * After login, we do a full page reload, it will re-trigger the useEffect
   */
  const fetchUserConfig = async () => {
    if (CreditBookApi.tokenManager.isAuthenticated()) {
      try {
        const {data} = await CreditBookApi.userConfig.getUserConfig();
        UserConfigManager.setConfig(data);
      } catch {
        setError(true);
      } finally {
        setLoadingUserConfig(false);
      }
    } else {
      setLoadingUserConfig(false);
    }
  };

  // Portal config
  useEffect(() => {
    if (!loadingPortalConfig) {
      return;
    }
    fetchPortalConfig();
  }, [loadingPortalConfig]);

  // User config
  useEffect(() => {
    if (!loadingUserConfig) {
      return;
    }
    fetchUserConfig();
  }, [loadingUserConfig]);

  if (error) {
    return (
      <Container>
        <Wrapper>
          <Title>{t('global_error')}</Title>
          <p>{t('global_errorOccurred')}</p>
        </Wrapper>
      </Container>
    );
  }

  if (loadingUserConfig || loadingPortalConfig) {
    return <LoadingPage />;
  }

  return <>{children}</>;
}

export default ConfigLoader;
